/**
 * RoofMapper - Main Component
 * Portable Solar Analysis Module for NIKO Energy
 */

'use client';

import React, { useEffect } from 'react';
import FullscreenLayout from './components/layout/FullscreenLayout';
import type { RoofMapperProps } from './types';
import { useRoofMapperContext } from './contexts/RoofMapperContext';

export function RoofMapper({
  mode = 'fullscreen',
  width,
  height,
  features,
  initialLocation,
  initialMapMode,
  onLocationSelect,
  onSolarDataFetched,
  onManualMeasurement,
  onSave,
  onError,
  onMapReady,
  rpu,
  projectId,
  customData,
  className,
  style,
}: RoofMapperProps) {
  const context = useRoofMapperContext();

  // Set initial location if provided
  useEffect(() => {
    if (initialLocation) {
      context.setLocation({
        ...initialLocation,
        timestamp: new Date(),
      });
    }
  }, [initialLocation]);

  // Set initial map mode if provided
  useEffect(() => {
    if (initialMapMode) {
      context.setMapMode(initialMapMode);
    }
  }, [initialMapMode]);

  // Handle mode-specific rendering
  const renderLayout = () => {
    switch (mode) {
      case 'fullscreen':
        return <FullscreenLayout />;

      case 'embedded':
        // TODO: Implement EmbeddedLayout
        return (
          <div
            className={className}
            style={{ width, height, ...style }}
          >
            <FullscreenLayout />
          </div>
        );

      case 'modal':
        // TODO: Implement ModalLayout
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-[90vw] h-[90vh] max-w-7xl">
              <FullscreenLayout />
            </div>
          </div>
        );

      case 'sidebar':
        // TODO: Implement SidebarLayout
        return (
          <div
            className={className}
            style={{ width: width || '400px', height: height || '100vh', ...style }}
          >
            <FullscreenLayout />
          </div>
        );

      default:
        return <FullscreenLayout />;
    }
  };

  return renderLayout();
}

export default RoofMapper;
